import { useCallback, useMemo, useState } from 'react';

import { faCalendar } from '@fortawesome/free-regular-svg-icons';
import {
  faArrowDown,
  faBan,
  faDollarSign,
  faExclamationTriangle,
  faHourglass,
  faTooth,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@repo/videa-components/src/index';
import { useAtom } from 'jotai';

import type {
  ZuubEligibilityData,
  ZuubEligibilityFlagDetail,
  ZuubEligibilityProcedure,
  ZuubEligibilityStatus,
  ZuubProcedureFlag,
} from '../../../@types/zuub/contracts';
import { usePreferences } from '../../../react-query/usePreferences';
import { benefitsNetworkSelectionsByPayerAtom } from '../atoms';
import type { AutoVerifyTableRow } from '../helpers';
import { isOutOfNetworkName } from '../helpers';

// ── Status → Display Mapping ───────────────────────────────────────────────────

const getStatusDisplay = (
  status: ZuubEligibilityStatus,
): { label: string; style: string } => {
  switch (status) {
    case 'ELIGIBLE':
      return {
        label: 'Eligible',
        style: 'vai-bg-green-50 vai-text-green-700',
      };
    case 'INELIGIBLE':
      return {
        label: 'Ineligible',
        style: 'vai-bg-red-50 vai-text-red-700',
      };
    case 'PARTIALLY_COVERED':
      return {
        label: 'Partially Covered',
        style: 'vai-bg-amber-50 vai-text-amber-700',
      };
    case 'UNKNOWN':
    default:
      return {
        label: 'Unknown',
        style: 'vai-bg-gray-100 vai-text-gray-600',
      };
  }
};

// ── Flag → Icon / Title Mapping ────────────────────────────────────────────────

const getFlagIcon = (flagType: ZuubProcedureFlag | string) => {
  switch (flagType) {
    case 'FREQUENCY_LIMITATION':
      return faHourglass;
    case 'ALTERNATE_BENEFIT':
      return faArrowDown;
    case 'WAITING_PERIOD':
      return faCalendar;
    case 'MISSING_TOOTH_CLAUSE':
      return faTooth;
    case 'AGE_RESTRICTION':
      return faExclamationTriangle;
    case 'PRE_AUTH_REQUIRED':
    case 'PRE_AUTH_RECOMMENDED':
      return faExclamationTriangle;
    case 'BENEFIT_PERIOD_EXPIRED':
      return faCalendar;
    case 'MAXIMUM_EXHAUSTED':
      return faDollarSign;
    case 'NOT_COVERED':
      return faBan;
    default:
      return faExclamationTriangle;
  }
};

const getFlagTitle = (flagType: ZuubProcedureFlag | string): string => {
  switch (flagType) {
    case 'FREQUENCY_LIMITATION':
      return 'FREQUENCY LIMITATION';
    case 'WAITING_PERIOD':
      return 'WAITING PERIOD';
    case 'MISSING_TOOTH_CLAUSE':
      return 'MISSING TOOTH CLAUSE';
    case 'ALTERNATE_BENEFIT':
      return 'ALTERNATE BENEFIT';
    case 'AGE_RESTRICTION':
      return 'AGE RESTRICTION';
    case 'PRE_AUTH_REQUIRED':
      return 'PRE-AUTH REQUIRED';
    case 'PRE_AUTH_RECOMMENDED':
      return 'PRE-AUTH RECOMMENDED';
    case 'BENEFIT_PERIOD_EXPIRED':
      return 'BENEFIT PERIOD EXPIRED';
    case 'MAXIMUM_EXHAUSTED':
      return 'MAXIMUM EXHAUSTED';
    case 'NOT_COVERED':
      return 'NOT COVERED';
    default:
      return flagType.replace(/_/g, ' ');
  }
};

// ── Formatting helpers ─────────────────────────────────────────────────────────

/** Convert "BasicRestorative" → "Basic Restorative" */
const formatCategory = (category: string | null | undefined): string => {
  if (!category) return '—';
  return category.replace(/([a-z])([A-Z])/g, '$1 $2');
};

/** Format location string: "30" → "#30", null/undefined → "—" */
const formatLocation = (location?: string | null): string => {
  if (!location) return '—';
  return `#${location}`;
};

// ── Dropdown Component ─────────────────────────────────────────────────────────
const NetworkDropdown = ({
  options,
  value,
  onChange,
}: {
  options: string[];
  value: string;
  onChange: (val: string) => void;
}) => (
  <div className="vai-relative vai-inline-flex vai-items-center">
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      onClick={(e) => e.stopPropagation()}
      className="vai-appearance-none vai-bg-white vai-border vai-border-gray-200 vai-rounded-lg vai-px-3 vai-py-1.5 vai-pr-8 vai-text-sm vai-font-medium vai-text-gray-700 vai-cursor-pointer hover:vai-border-gray-300 focus:vai-outline-none focus:vai-ring-2 focus:vai-ring-blue-200 focus:vai-border-blue-400"
    >
      {options.map((opt) => (
        <option key={opt} value={opt}>
          {opt}
        </option>
      ))}
    </select>
    <svg
      className="vai-absolute vai-right-2.5 vai-top-1/2 vai--translate-y-1/2 vai-w-3 vai-h-3 vai-text-gray-400 vai-pointer-events-none"
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M19 9l-7 7-7-7"
      />
    </svg>
  </div>
);

// ── Aggregated summary insight (derived from procedure flagDetails) ────────────
interface SummaryInsight {
  type: string;
  title: string;
  messages: string[];
  codes: string[];
}

// ── Component ──────────────────────────────────────────────────────────────────
interface ExpandedPatientRowProps {
  row: AutoVerifyTableRow;
}

export const ExpandedPatientRow = ({ row }: ExpandedPatientRowProps) => {
  const { data: prefs } = usePreferences();
  const defaultOutOfNetwork = prefs?.defaultOutOfNetwork ?? false;
  const [networkSelectionsByPayer, setNetworkSelectionsByPayer] = useAtom(
    benefitsNetworkSelectionsByPayerAtom,
  );

  const payerId = row.plans[0]?.payerId;
  const primaryProviderId = row.zuubPatient.primaryProviderId;
  const persistKey =
    payerId != null ? `${payerId}:${primaryProviderId ?? ''}` : null;

  const eligibilityEntries = useMemo<ZuubEligibilityData[]>(
    () => row.plans[0]?.eligibility ?? [],
    [row.plans],
  );

  // Derive available network names from the array of entries
  const networkNames = useMemo(
    () => eligibilityEntries.map((e) => e.network),
    [eligibilityEntries],
  );

  const [selectedNetwork, setSelectedNetworkLocal] = useState(() => {
    // 1. Restore from persisted payer+provider selection
    if (persistKey) {
      const saved = networkSelectionsByPayer[persistKey];
      if (saved && networkNames.includes(saved)) return saved;
    }
    // 2. Fall back to OON if pref is set
    if (defaultOutOfNetwork) {
      const oonName = networkNames.find((n) => isOutOfNetworkName(n));
      if (oonName) return oonName;
    }
    // 3. Default to first network
    return networkNames[0] ?? '';
  });

  const setSelectedNetwork = useCallback(
    (network: string) => {
      setSelectedNetworkLocal(network);
      if (persistKey && network.length > 0) {
        setNetworkSelectionsByPayer((prev) => ({
          ...prev,
          [persistKey]: network,
        }));
      }
    },
    [persistKey, setNetworkSelectionsByPayer],
  );

  // Pick the matching eligibility entry for the selected network
  const selectedEntry = useMemo<ZuubEligibilityData | undefined>(
    () =>
      eligibilityEntries.find((e) => e.network === selectedNetwork) ??
      eligibilityEntries[0],
    [eligibilityEntries, selectedNetwork],
  );

  // Procedures come directly from the selected entry (already scoped to network)
  const filteredProcedures = useMemo(
    () => selectedEntry?.procedures ?? [],
    [selectedEntry],
  );

  // Aggregate flagDetails across filtered procedures for coverage summary
  const summaryInsights = useMemo<SummaryInsight[]>(() => {
    if (filteredProcedures.length === 0) return [];
    const flagMap = new Map<
      string,
      { type: string; messages: string[]; codes: string[] }
    >();

    filteredProcedures.forEach((proc) => {
      proc.flagDetails?.forEach((detail: ZuubEligibilityFlagDetail) => {
        if (!flagMap.has(detail.type)) {
          flagMap.set(detail.type, {
            type: detail.type,
            messages: [],
            codes: [],
          });
        }
        const entry = flagMap.get(detail.type)!;
        if (!entry.messages.includes(detail.message)) {
          entry.messages.push(detail.message);
        }
        if (!entry.codes.includes(proc.code)) {
          entry.codes.push(proc.code);
        }
      });
    });

    return Array.from(flagMap.values()).map((entry) => ({
      ...entry,
      title: getFlagTitle(entry.type),
    }));
  }, [filteredProcedures]);

  // Derive "Patient Financials" insight from filtered procedures with partial coverage
  const financialsInsight = useMemo<string | null>(() => {
    if (filteredProcedures.length === 0) return null;
    const partial = filteredProcedures.filter(
      (p) =>
        (p.status === 'ELIGIBLE' || p.status === 'PARTIALLY_COVERED') &&
        p.coinsurancePct !== undefined &&
        p.coinsurancePct < 100,
    );
    if (partial.length === 0) return null;
    const codes = partial.map((p) => p.code).join(', ');
    return `${partial.length} treatment${partial.length > 1 ? 's' : ''} with partial coverage (${codes}). Review estimated out-of-pocket with patient.`;
  }, [filteredProcedures]);

  // ── No eligibility data ────────────────────────────────────────────────────
  if (eligibilityEntries.length === 0) {
    return (
      <div className="vai-px-8 vai-py-8 vai-text-center vai-text-gray-500 vai-text-sm">
        No eligibility data available for this patient.
      </div>
    );
  }

  const procedures = filteredProcedures;
  const globalWarnings = selectedEntry?.globalWarnings ?? [];

  return (
    <div className="vai-flex vai-gap-0">
      {/* ── Left: Scheduled Treatments ───────────────────────────────────── */}
      <div className="vai-flex-1 vai-px-8 vai-py-5 vai-border-r vai-border-gray-100">
        <div className="vai-flex vai-items-center vai-justify-between vai-mb-4">
          <h4 className="vai-text-[11px] vai-font-semibold vai-text-gray-400 vai-tracking-widest vai-uppercase">
            Scheduled Treatments
          </h4>
          <NetworkDropdown
            options={networkNames}
            value={selectedNetwork}
            onChange={setSelectedNetwork}
          />
        </div>

        <div className="vai-border vai-border-gray-200 vai-rounded-lg vai-overflow-hidden">
          {/* Table header */}
          <div className="vai-grid vai-grid-cols-[80px_1fr_60px_120px_90px_140px_40px] vai-bg-gray-50 vai-px-4 vai-py-2 vai-border-b vai-border-gray-200">
            {[
              'CODE',
              'SERVICE',
              'LOC.',
              'CATEGORY',
              'COVERAGE',
              'ELIGIBILITY',
              '',
            ].map((header) => (
              <span
                key={header || 'actions'}
                className="vai-text-[11px] vai-font-semibold vai-text-gray-400 vai-uppercase vai-tracking-wider"
              >
                {header}
              </span>
            ))}
          </div>

          {/* Treatment rows */}
          {procedures.map((proc: ZuubEligibilityProcedure, idx: number) => {
            const statusDisplay = getStatusDisplay(proc.status);
            return (
              <div
                key={`${proc.code}-${proc.location ?? ''}-${idx}`}
                className={`vai-grid vai-grid-cols-[80px_1fr_60px_120px_90px_140px_40px] vai-px-4 vai-py-2.5 vai-items-center ${
                  idx < procedures.length - 1
                    ? 'vai-border-b vai-border-gray-100'
                    : ''
                }`}
              >
                <span className="vai-text-sm vai-font-medium vai-text-gray-900">
                  {proc.code}
                </span>
                <span className="vai-text-sm vai-text-gray-700">
                  {proc.description}
                </span>
                <span className="vai-text-sm vai-text-gray-500">
                  {formatLocation(proc.location)}
                </span>
                <span className="vai-text-sm vai-text-gray-600">
                  {formatCategory(proc.category)}
                </span>
                <span className="vai-text-sm vai-text-gray-800">
                  {proc.coinsurancePct !== undefined
                    ? `${proc.coinsurancePct}%`
                    : '—'}
                </span>

                {/* Compact eligibility pill */}
                <span
                  className={`vai-inline-flex vai-items-center vai-px-2.5 vai-py-0.5 vai-rounded-full vai-text-xs vai-font-medium vai-w-fit ${statusDisplay.style}`}
                >
                  {statusDisplay.label}
                </span>

                {/* Flag icons with tooltips from flagDetails */}
                <div className="vai-flex vai-items-center vai-gap-2.5">
                  {proc.flagDetails?.map(
                    (detail: ZuubEligibilityFlagDetail, fIdx: number) => (
                      <Tooltip key={`${detail.type}-${fIdx}`} delayDuration={0}>
                        <TooltipTrigger
                          asChild
                          onClick={(e) => e.stopPropagation()}
                        >
                          <span className="vai-cursor-default">
                            <FontAwesomeIcon
                              icon={getFlagIcon(detail.type)}
                              className="vai-w-3 vai-h-3 vai-text-gray-400"
                            />
                          </span>
                        </TooltipTrigger>
                        <TooltipContent
                          side="top"
                          className="vai-text-sm vai-max-w-xs"
                        >
                          {detail.message}
                        </TooltipContent>
                      </Tooltip>
                    ),
                  )}
                </div>
              </div>
            );
          })}

          {procedures.length === 0 && (
            <div className="vai-px-4 vai-py-6 vai-text-center vai-text-sm vai-text-gray-400">
              No scheduled procedures found.
            </div>
          )}
        </div>
      </div>

      {/* ── Right: Coverage Summary ──────────────────────────────────────── */}
      <div className="vai-w-[300px] vai-flex-shrink-0 vai-px-5 vai-py-5 vai-overflow-y-auto">
        <h4 className="vai-text-[11px] vai-font-semibold vai-text-gray-400 vai-tracking-widest vai-uppercase vai-mb-4">
          Coverage Summary
        </h4>

        <div className="vai-flex vai-flex-col vai-gap-4">
          {/* ── Flag-derived insight cards ─────────────────────────────────── */}
          {summaryInsights.map((insight) => (
            <div
              key={insight.type}
              className="vai-bg-gray-50 vai-rounded-lg vai-p-4"
            >
              <div className="vai-flex vai-items-center vai-gap-2 vai-mb-2">
                <FontAwesomeIcon
                  icon={getFlagIcon(insight.type)}
                  className="vai-w-3 vai-h-3 vai-text-gray-400"
                />
                <span className="vai-text-[11px] vai-font-semibold vai-text-gray-400 vai-tracking-wider vai-uppercase">
                  {insight.title}
                </span>
              </div>
              <div className="vai-text-sm vai-text-gray-600 vai-leading-relaxed vai-pl-5">
                {insight.messages.map((msg, i) => (
                  <p key={i} className={i > 0 ? 'vai-mt-1' : ''}>
                    {msg}
                  </p>
                ))}
              </div>
            </div>
          ))}

          {/* ── Patient Financials (derived) ───────────────────────────────── */}
          {financialsInsight && (
            <div className="vai-bg-gray-50 vai-rounded-lg vai-p-4">
              <div className="vai-flex vai-items-center vai-gap-2 vai-mb-2">
                <FontAwesomeIcon
                  icon={faDollarSign}
                  className="vai-w-3 vai-h-3 vai-text-gray-400"
                />
                <span className="vai-text-[11px] vai-font-semibold vai-text-gray-400 vai-tracking-wider vai-uppercase">
                  PATIENT FINANCIALS
                </span>
              </div>
              <p className="vai-text-sm vai-text-gray-600 vai-leading-relaxed vai-pl-5">
                {financialsInsight}
              </p>
            </div>
          )}

          {/* ── Global Warnings ────────────────────────────────────────────── */}
          {globalWarnings.length > 0 && (
            <div className="vai-bg-amber-50/50 vai-rounded-lg vai-p-4">
              <div className="vai-flex vai-items-center vai-gap-2 vai-mb-2">
                <FontAwesomeIcon
                  icon={faExclamationTriangle}
                  className="vai-w-3 vai-h-3 vai-text-amber-500"
                />
                <span className="vai-text-[11px] vai-font-semibold vai-text-amber-600 vai-tracking-wider vai-uppercase">
                  WARNINGS
                </span>
              </div>
              <ul className="vai-text-sm vai-text-gray-600 vai-leading-relaxed vai-pl-5 vai-space-y-1">
                {globalWarnings.map((warning, i) => (
                  <li key={i}>{warning}</li>
                ))}
              </ul>
            </div>
          )}

          {/* ── Empty state ─────────────────────────────────────────────────── */}
          {summaryInsights.length === 0 &&
            !financialsInsight &&
            globalWarnings.length === 0 && (
              <p className="vai-text-sm vai-text-gray-400 vai-text-center vai-py-4">
                No coverage insights for this check.
              </p>
            )}
        </div>
      </div>
    </div>
  );
};
